import React from 'react';
import { Box, Typography } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import { useInstatDesign } from '../theme/instatDesign';

/**
 * A KPI tile: big value on top, label below, optional trend arrow.
 *
 * Props:
 *  - label (string)
 *  - value (string|number)
 *  - trend: { direction: 'up'|'down'|'flat', delta?: string|number }
 *  - accent: if true, uses club accent gradient for the number
 *  - compact: smaller font
 *  - onClick: makes the tile hoverable
 */
export default function KpiTile({
  label,
  value,
  trend,
  accent = false,
  compact = false,
  onClick,
  suffix,
  sx = {},
}) {
  const d = useInstatDesign();
  const clickable = Boolean(onClick);

  const trendColor =
    trend?.direction === 'up'
      ? d.colors.success
      : trend?.direction === 'down'
      ? d.colors.danger
      : d.colors.textTertiary;
  const TrendIcon =
    trend?.direction === 'up' ? ArrowUpwardIcon
    : trend?.direction === 'down' ? ArrowDownwardIcon
    : RemoveIcon;

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        bgcolor: d.colors.surface,
        borderRadius: `${d.radius.lg}px`,
        px: compact ? 1.5 : 2,
        py: compact ? 1.25 : 1.75,
        boxShadow: d.shadows.card,
        border: `1px solid ${d.colors.divider}`,
        cursor: clickable ? 'pointer' : 'default',
        transition: `transform ${d.motion.base}, box-shadow ${d.motion.base}`,
        ...(clickable && {
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: d.shadows.cardHover,
          },
        }),
        ...sx,
      }}
    >
      <Typography
        sx={{
          ...d.typo.micro,
          color: d.colors.textSecondary,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography
          component="span"
          sx={{
            fontSize: compact ? 22 : 28,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            lineHeight: 1,
            ...d.typo.tabular,
            color: d.colors.text,
            background: accent ? d.accentGradient : 'none',
            WebkitBackgroundClip: accent ? 'text' : 'initial',
            WebkitTextFillColor: accent ? 'transparent' : 'initial',
          }}
        >
          {value}
        </Typography>
        {suffix && (
          <Typography
            component="span"
            sx={{ fontSize: 13, color: d.colors.textSecondary, fontWeight: 500 }}
          >
            {suffix}
          </Typography>
        )}
      </Box>

      {trend && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            mt: 0.75,
            color: trendColor,
          }}
        >
          <TrendIcon sx={{ fontSize: 12 }} />
          <Typography
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              ...d.typo.tabular,
              color: trendColor,
            }}
          >
            {trend.delta != null ? trend.delta : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
